/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import type { RequestedLib } from 'firefox-profiler/types';

// Used during the symbolication process to express that we couldn't find
// symbols for a specific library
export class SymbolsNotFoundError extends Error {
  library: RequestedLib;
  errors: Error[];

  constructor(message: string, library: RequestedLib, ...errors: Error[]) {
    super(
      [message, ...errors.map((e) => ` - ${e.name}: ${e.message}`)].join('\n')
    );
    // Workaround for a babel issue when extending Errors
    (this as any).__proto__ = SymbolsNotFoundError.prototype;
    this.name = 'SymbolsNotFoundError';
    this.library = library;
    this.errors = errors;
  }
}

// Thrown when a profile's format version is newer than the most recent version
// understood by this build. The message is deliberately neutral and only states
// the facts. Consumers (the web app, the CLI) detect this by name and append
// their own advice on how to update, since that advice is frontend-specific.
export class ProfileVersionError extends Error {
  formatName: string;
  profileVersion: number;
  supportedVersion: number;

  constructor(
    formatName: string,
    profileVersion: number,
    supportedVersion: number
  ) {
    super(
      `Unable to parse a ${formatName} profile of version ${profileVersion}. ` +
        `The most recent version understood by this build is version ${supportedVersion}.`
    );
    // Workaround for a babel issue when extending Errors
    (this as any).__proto__ = ProfileVersionError.prototype;
    this.name = 'ProfileVersionError';
    this.formatName = formatName;
    this.profileVersion = profileVersion;
    this.supportedVersion = supportedVersion;
  }
}
